import { useEffect, useRef, useState } from 'react';
import { cn } from '../cn.js';
import { ClockDigits } from './ClockDigits.js';
import { formatDuration, type TimerFormat } from './internal/formatDuration.js';

export interface CountdownTimerProps {
  /** The instant counted down to, e.g. a training task's `dueAt`. */
  deadline: Date;
  /** `HH:MM:SS` (default) for a same-day deadline; `Dd HH:MM` once the remaining time can span
   *  days. */
  format?: TimerFormat;
  className?: string;
  /** Overrides the generic "Time remaining" aria-label. */
  ariaLabel?: string;
  /** Called once, on the first tick the countdown reaches zero — e.g. to flip a training overrun
   *  banner from "time remaining" to "overdue" messaging. Never called again after that. */
  onExpire?: () => void;
}

/**
 * A duration counted down to `deadline`, stopping at `00:00:00` rather than going negative.
 * Ticks once a second via its own `setInterval`, cleaned up on unmount and also stopped internally
 * the moment the deadline passes — a countdown has nothing left to tell the viewer past zero, so
 * there is no reason to keep re-rendering it once the gate it is timing has already tripped.
 *
 * See `ElapsedTimer`'s doc comment for why the displayed value is always recomputed from
 * `deadline`/`Date.now()` rather than accumulated in state, and for how the digits are typeset.
 */
export function CountdownTimer({
  deadline,
  format = 'HH:MM:SS',
  className,
  ariaLabel = 'Time remaining',
  onExpire,
}: CountdownTimerProps) {
  const [now, setNow] = useState(() => Date.now());
  const expiredRef = useRef(false);

  useEffect(() => {
    expiredRef.current = false;
    function tick() {
      const currentNow = Date.now();
      setNow(currentNow);
      if (currentNow >= deadline.getTime()) clearInterval(intervalId);
    }
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [deadline]);

  const remainingMs = deadline.getTime() - now;

  useEffect(() => {
    if (remainingMs <= 0 && !expiredRef.current) {
      expiredRef.current = true;
      onExpire?.();
    }
  }, [remainingMs, onExpire]);

  return (
    <span role="timer" aria-label={ariaLabel} className={cn('inline-flex', className)}>
      <ClockDigits value={formatDuration(remainingMs / 1000, format)} live={remainingMs > 0} />
    </span>
  );
}
